import { CompartmentSelector, Finder, SaveFinderPayload, Template } from '@ekkogmbh/apisdk';
import { Box, Card, CardContent, InputAdornment, Stack, Typography } from '@mui/material';
import {
  ShopfloorAppPreview,
  ShopfloorPromptText,
  ShopfloorInputField,
  ShopfloorResultsDialog,
} from 'src/Common/Components/ShopfloorAppPreview';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from '../../Common/Components/CoordinateInput';
import { NotificationStrategyForm } from 'src/Common/Components/Forms/NotificationStrategyForm';
import { CompartmentSelectorPicker } from 'src/CompartmentSelectorManagement/CompartmentSelectorPicker';
import { TemplatePicker } from 'src/TemplateManagement/TemplatePicker';
import { ContentPanel, StepDefinition, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import { ReactivePanelColumn } from 'src/Common/Components/Stacks';
import useNotificationStrategy from 'src/ZoneManagement/Providers/NotificationStrategyProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';

interface Props {
  finder?: Finder;
  closeHandler: () => void;
}

export const FinderPanel = ({ finder, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { refresh } = usePagination();
  const { currentStep, enableSave, enableNext } = usePanel();

  const { notificationStrategy, setStrategies: setNotificationStrategy } = useNotificationStrategy();

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [templateNames, setTemplateNames] = useState<string[]>([]);
  const [compartmentSelector, setCompartmentSelector] = useState<CompartmentSelector>();
  const [showResultsDuration, setShowResultsDuration] = useState<number | ''>();
  const [compartmentInputInstructions, setCompartmentInputInstructions] = useState<string>('');
  const [promptFocused, setPromptFocused] = useState<boolean>(false);
  const [durationFocused, setDurationFocused] = useState<boolean>(false);

  const isEditMode = useMemo(() => finder !== undefined, [finder]);
  const [isSaving, startSaving] = useRequest<Finder>();

  useEffect(() => {
    const isIdSet = ![name, coordinate].includes('');

    enableSave(isIdSet);
    switch (currentStep) {
      case 0:
        enableNext(isIdSet);
        break;
      default:
        enableNext(true);
    }
  }, [currentStep, name, coordinate]);

  const payload = useMemo((): SaveFinderPayload => {
    const payload: SaveFinderPayload = {
      notificationStrategy: notificationStrategy!,
      compartmentInputInstructions: compartmentInputInstructions !== '' ? compartmentInputInstructions : null,
      showResultsDuration: [0, '', undefined].includes(showResultsDuration) ? null : Number(showResultsDuration),
      templateNames,
    };
    if (compartmentSelector !== undefined) {
      payload.compartmentSelector = {
        name: compartmentSelector.name,
        coordinate: compartmentSelector.coordinate,
      };
    }
    return payload;
  }, [compartmentSelector, compartmentInputInstructions, notificationStrategy, showResultsDuration, templateNames]);

  const reset = useCallback(() => {
    setName(finder?.name ?? '');
    setCoordinate(finder?.coordinate ?? '');
    setTemplateNames(finder?.templateNames ?? []);
    setCompartmentSelector(finder?.compartmentSelector);
    setNotificationStrategy(finder?.notificationStrategy);
    setCompartmentInputInstructions(finder?.compartmentInputInstructions ?? '');
    setShowResultsDuration(finder?.showResultsDuration ?? '');
  }, [finder]);

  useEffect(() => {
    reset();
  }, [reset]);

  const saveFinder = useCallback(() => {
    startSaving(
      () => api.saveFinder(coordinate, name, payload, isEditMode),
      () => {
        refresh();
        closeHandler();
      },
    );
  }, [name, coordinate, payload, isEditMode, closeHandler]);

  const idStep = useMemo(
    (): StepDefinition => ({
      title: 'Finder',
      contentCallback: () => (
        <ReactivePanelColumn>
          <StyledFormHeader label="Finder" />
          <StyledTextField
            type="text"
            label="Name"
            value={name}
            disabled={isEditMode}
            onChange={(e) => setName(String(e.target.value))}
          />
          <CoordinateInput
            value={coordinate}
            disabled={isEditMode}
            onChange={setCoordinate}
            trailingDelimiter={false}
          />
        </ReactivePanelColumn>
      ),
    }),
    [name, coordinate, isEditMode],
  );

  const deviceSelectionStep = useMemo(
    (): StepDefinition => ({
      title: 'Device Selection',
      contentCallback: () => (
        <ReactivePanelColumn>
          <StyledFormHeader label="Device Selection" description="Define which devices the finder will search for" />
          <Stack spacing={0.5}>
            <CompartmentSelectorPicker
              optional
              coordinate={coordinate}
              selected={compartmentSelector}
              onChange={setCompartmentSelector}
            />
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5 }}>
              Limits the search to compartments matching the selector
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            <TemplatePicker
              multiple
              coordinate={coordinate}
              selected={templateNames}
              onChangeCallback={(selection) => {
                const names = (selection as (Template | string)[]).map((t) => {
                  if (typeof t === 'string') {
                    return t;
                  }

                  return t.name;
                });
                setTemplateNames(names);
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5 }}>
              Filter by device template — leave empty to include all devices
            </Typography>
          </Stack>
        </ReactivePanelColumn>
      ),
    }),
    [coordinate, compartmentSelector, templateNames],
  );

  const shopfloorAppStep = useMemo(
    (): StepDefinition => ({
      title: 'Shopfloor App',
      contentCallback: () => (
        <Stack width={'98%'} alignItems="center" spacing={2}>
          <Box sx={{ width: { xs: '100%', lg: '70%' } }}>
            <Stack spacing={2}>
              <StyledFormHeader label="Shopfloor App" description="Configure Shopfloor App appearance" />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="center">
                {/* Input Prompt section */}
                <Card variant="outlined" sx={{ flex: 1 }}>
                  <CardContent>
                    <Stack spacing={2} alignItems="center">
                      <Typography variant="overline" color="primary" fontWeight={700}>
                        Search Screen
                      </Typography>
                      <ShopfloorAppPreview
                        moduleName="Finder"
                        entityName={name || 'Finder Name'}
                        coordinate={coordinate || 'coordinate'}
                      >
                        <ShopfloorPromptText
                          text={compartmentInputInstructions}
                          defaultText="Please scan to find compartments"
                          focused={promptFocused}
                        />
                        <ShopfloorInputField label="Compartment" />
                      </ShopfloorAppPreview>
                      <StyledTextField
                        type="text"
                        label="Input Prompt"
                        tooltip="Text shown before entering a search term."
                        placeholder='e.g. "Enter product code"'
                        value={compartmentInputInstructions}
                        onChange={(e) => setCompartmentInputInstructions(String(e.target.value))}
                        onFocus={() => setPromptFocused(true)}
                        onBlur={() => setPromptFocused(false)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>
                  </CardContent>
                </Card>

                {/* Results Duration section */}
                <Card variant="outlined" sx={{ flex: 1 }}>
                  <CardContent>
                    <Stack spacing={2} alignItems="center">
                      <Typography variant="overline" color="primary" fontWeight={700}>
                        Results Screen
                      </Typography>
                      <ShopfloorAppPreview
                        moduleName="Finder"
                        entityName={name || 'Finder Name'}
                        coordinate={coordinate || 'coordinate'}
                        overlay={<ShopfloorResultsDialog duration={showResultsDuration} focused={durationFocused} />}
                      />
                      <StyledTextField
                        type="number"
                        label="Results Duration"
                        value={showResultsDuration}
                        tooltip="Results panel will close automatically after the set duration."
                        onChange={(e) => {
                          const inputNumber = Number(e.target.value);
                          if (inputNumber <= 0 || isNaN(inputNumber)) {
                            setShowResultsDuration('');
                          } else {
                            setShowResultsDuration(Math.min(inputNumber, 2147483));
                          }
                        }}
                        onFocus={() => setDurationFocused(true)}
                        onBlur={() => setDurationFocused(false)}
                        endAdornment={<InputAdornment position="end">sec</InputAdornment>}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      ),
    }),
    [name, coordinate, compartmentInputInstructions, showResultsDuration, promptFocused, durationFocused],
  );

  const notificationStep = useMemo(
    (): StepDefinition => ({
      title: 'Notification',
      contentCallback: () => (
        <ReactivePanelColumn lgWidth={'50%'}>
          <StyledFormHeader label="Notification" description="Configure how devices notify users when found" />
          <NotificationStrategyForm />
        </ReactivePanelColumn>
      ),
    }),
    [],
  );

  return (
    <ContentPanel
      isLoading={isSaving}
      initialStep={isEditMode ? 1 : 0}
      steps={[idStep, deviceSelectionStep, notificationStep, shopfloorAppStep]}
      onCancel={closeHandler}
      onSave={saveFinder}
      onReset={reset}
    />
  );
};
